import {
  type AbstractTemplateInOut,
  type BranchChangesets,
  type ChangesetHistory,
  type ConfigurationExpressionValueInOut,
  type ConfigurationFunctionValueInOut,
  type ConfigurationOperation,
  type EnumeratedValueFrameInOut,
  isConfigurationExpressionValueInOut,
  isConfigurationFunctionValueInOut,
  isEnumeratedValueFrameInOut,
  isLargeOperation,
  isRangedValueFrameInOut,
  isSimpleValueSetupInOut,
  isSubTemplateInOut,
  isSubTemplateValueSetupInOut,
  isTemplateCreateOperation,
  isTemplateValueSetupInOut,
  type RangedValueFrameInOut,
  type SubTemplateInOut,
  type SubTemplateValueSetupInOut,
  type TemplateFieldValueInOut,
  type TemplateInOut,
  type TemplatePropertyInOut,
  type TemplateValueSetupInOut,
  type TemplateVariantInOut,
  type ValidationInOut,
  type ValueFrameInOut,
  type ValueSetupInOut,
} from '@festina/configuration-general-types/configuration'
import type { ConfigurationPackage } from '@festina/life-general-types/life'
import { type LayerKey, OperationLayerMap } from '@festina-life/api'
import {
  assertNotEmpty,
  dateUtils,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import cloneDeep from 'lodash-es/cloneDeep'

import type {
  MetaModel,
  ModelMetaType,
  PropertyDescriptor,
} from '../types/metaModel'
import type { ConfigurationItemsByBlock } from '../workspace/operationsContext'
import type { IndexedParameterDefinitionCreateOperationWithMetaData } from './parameterOperations/types'
import {
  getTemplateOperationFromDate,
  getTemplateProperty,
} from './templateOperations/helpers'
import {
  type IndexedTemplateOperationWithMetaData,
  isNotInherited,
  type TemplateOperation,
} from './templateOperations/types'
import { functionPath } from './transformers/toVisualModelConverter'
import {
  ENRICHABLE_RAW_KEYS,
  type Enriched,
  type FieldOrigin,
  type TemplateFunctionKeys,
  type UserActionData,
  type VersionedValue,
  type WithEnrichedRawTypes,
} from './types'

export const ensureIndexedBlockLayerEntity = <T>({
  allResults,
  block,
  layer,
  entityId,
  createDefault,
}: {
  allResults: ConfigurationItemsByBlock<T>
  block: string
  layer: LayerKey
  entityId: string
  createDefault: () => T
}): T => {
  const blockLayers =
    allResults.get(block) ?? new OperationLayerMap<Map<string, T>>()
  allResults.set(block, blockLayers)

  const layerResult = blockLayers.get(layer) ?? new Map<string, T>()
  blockLayers.set(layer, layerResult)

  if (!layerResult.has(entityId)) {
    layerResult.set(entityId, createDefault())
  }

  return layerResult.get(entityId)!
}

/**
 * Utility function for making it easy to conditionally transform and spread a field into an object and only declaring the field when its
 * value is not undefined (i.e. null values are therefore defined).
 */
export const optionallyPickAndMap = <
  MapTo,
  T extends Enriched<Record<string, unknown>>,
  K extends keyof T,
>({
  originalObject,
  key,
  mapFn,
  asKey,
  path,
  fieldOriginMapper = function defaultFieldOriginMapper() {
    return notEmpty(originalObject[key]) &&
      typeof originalObject[key] === 'object' &&
      'origin' in originalObject[key]
      ? (originalObject[key] as Record<'origin', Optional<FieldOrigin>>)[
          'origin'
        ]
      : null
  },
}: {
  originalObject: T
  key: K
  mapFn: (value: NonNullable<T[K]>) => MapTo
  asKey?: string
  path: Optional<string>
  fieldOriginMapper?: (value: T[K]) => Optional<FieldOrigin>
}) => {
  if (
    notEmpty(originalObject[key]) &&
    (!path ||
      isNotInherited({
        path,
        origin: fieldOriginMapper(originalObject[key]),
      }))
  ) {
    const mappedValue = mapFn(originalObject[key])

    return {
      [asKey ?? key]: notEmpty(mappedValue) ? mappedValue : null,
    }
  } else {
    return {}
  }
}

/**
 * Utility function for making it easy to conditionally copy and spread a field into an object and only declaring the field when its
 * value is not undefined (i.e. null values are therefore defined).
 */
export const optionallyPick = <
  T extends Enriched<Record<string, unknown>>,
  K extends keyof T,
>({
  originalObject,
  key,
  asKey,
  path,
}: {
  originalObject: T
  key: K
  asKey?: string
  path: string
}) =>
  optionallyPickAndMap({
    originalObject,
    key,
    mapFn: (val) => val,
    asKey,
    path,
  })

const ValidConfigurationTypeNames = [
  'SimpleValueSetup',
  'SingleSubTemplateValueSetup',
  'MultipleSubTemplateValueSetup',
  'TemplateValueSetup',
  'ConfigurationExpressionValue',
]

const ExcludedModelProperties = ['defaultSelected', 'selectable']

export const allPropertiesFromTemplateAndModel = <
  T extends AbstractTemplateInOut,
>(
  template: Optional<T>,
  metaModel: Optional<ModelMetaType>,
): Map<string, Optional<PropertyDescriptor>> => {
  const templatePropertyNames = (template?.properties ?? [])
    .map((property) => property?.name)
    .filter(notEmpty)
  const typePropertyNames = metaModel?.properties
    .filter((property) =>
      ValidConfigurationTypeNames.includes(property.typeName),
    )
    .filter((property) => !ExcludedModelProperties.includes(property.name))
    .map((property) => property.name)
    .filter((name) => !ENRICHABLE_RAW_KEYS.some((key) => key === name))
  const allPropertyNames = new Set([
    ...templatePropertyNames,
    ...(typePropertyNames ?? []),
  ])

  return new Map<string, Optional<PropertyDescriptor>>(
    Array.from(allPropertyNames).map(
      (propertyName) =>
        [
          propertyName,
          metaModel?.properties.find(
            (descriptor) => descriptor.name === propertyName,
          ),
        ] as const,
    ),
  )
}

/**
 * Function used for traversing a TemplateCreateOperation and enriching all value setups with information about the
 * version info from which they obtain their value. This is usefyl for knowing where a value comes from in flattened
 * templates.
 */
export const enrichTemplate = <T extends TemplateInOut | SubTemplateInOut>({
  metaModel,
  template,
  path,
}: {
  metaModel: MetaModel
  template: T
  path: string
}): Enriched<T> | undefined => {
  const enrichedTemplate = cloneDeep(template)

  assertNotEmpty(template.templateTypeName)

  const type = metaModel.types.get(template.templateTypeName)

  const allPropertiesMap = allPropertiesFromTemplateAndModel(
    enrichedTemplate,
    type as Optional<ModelMetaType>,
  )

  const fields = Array.from(allPropertiesMap.entries())
    .map(([propName]) => {
      const property = getTemplateProperty(enrichedTemplate, propName)
      const value = property?.value
      const fieldPath = `${path}.${propName}`

      if (value) {
        const enrichedFieldValue = enrichConfigurationField({
          value,
          metaModel,
          path: fieldPath,
        }) as Enriched<TemplateFieldValueInOut>

        return {
          ...property,
          origin: fieldPath,
          value: enrichedFieldValue,
        } satisfies Enriched<TemplatePropertyInOut>
      } else {
        return property
      }
    })
    .filter(notEmpty)

  if (enrichedTemplate.documentation) {
    ;(enrichedTemplate as WithEnrichedRawTypes<T>).documentation = {
      value: enrichedTemplate.documentation,
      origin: `${path}.documentation`,
    }
  }

  if (isSubTemplateInOut(enrichedTemplate)) {
    if (enrichedTemplate.parent) {
      ;(enrichedTemplate as WithEnrichedRawTypes<SubTemplateInOut>).parent = {
        value: enrichedTemplate.parent,
        origin: `${path}.parent`,
      }
    }

    if (enrichedTemplate.defaultSelected) {
      ;(
        enrichedTemplate.defaultSelected as Enriched<ConfigurationExpressionValueInOut>
      ).origin = `${path}.defaultSelected`
    }

    if (enrichedTemplate.selectable) {
      ;(
        enrichedTemplate.selectable as Enriched<ConfigurationExpressionValueInOut>
      ).origin = `${path}.selectable`
    }
  }

  return {
    ...enrichedTemplate,
    properties: fields,
    origin: path,
    ...enrichTemplateFunctions({
      sourceTemplate: enrichedTemplate,
      functionsKeyName: 'declaredFunctions',
      path,
    }),
    ...enrichTemplateFunctions({
      sourceTemplate: enrichedTemplate,
      functionsKeyName: 'dynamicFunctions',
      path,
    }),
  } as Enriched<T>
}

const enrichConfigurationFunctionValue = ({
  value,
  path,
}: {
  value: ConfigurationFunctionValueInOut
  path: string
}): Enriched<ConfigurationFunctionValueInOut> => {
  const { documentation, ...others } = value

  return {
    ...others,
    ...(documentation
      ? {
          documentation: {
            value: documentation,
            origin: `${path}.documentation`,
          } satisfies VersionedValue<string>,
        }
      : {}),
    origin: path,
  }
}

const enrichTemplateFunctions = <T extends TemplateInOut | SubTemplateInOut>({
  sourceTemplate,
  functionsKeyName,
  path,
}: {
  sourceTemplate: T
  functionsKeyName: TemplateFunctionKeys
  path: string
}):
  | Partial<Record<TemplateFunctionKeys, Enriched<T[TemplateFunctionKeys]>>>
  | undefined =>
  sourceTemplate[functionsKeyName]
    ? {
        [functionsKeyName]: sourceTemplate[functionsKeyName]
          ?.filter(notEmpty)
          .map((functionValue) =>
            enrichConfigurationFunctionValue({
              value: functionValue,
              path: functionPath({
                basePath: path,
                source: functionValue.function,
              }),
            }),
          ),
      }
    : {}

const enrichValue = <T extends Record<string, unknown>, K extends string>({
  key,
  value,
  path,
}: {
  key: K
  value: Optional<T>
  path: string
}): Partial<Record<K, Enriched<T>>> => {
  if (!value) {
    return {}
  }

  let convertedValue = cloneDeep(value)

  ENRICHABLE_RAW_KEYS.forEach((rawKey) => {
    if (rawKey in convertedValue) {
      convertedValue = {
        ...convertedValue,
        [rawKey]: {
          value: convertedValue[rawKey],
          origin: `${path}.${key}.${rawKey}`,
        } as VersionedValue<(typeof convertedValue)[typeof rawKey]>,
      }
    }
  })

  return {
    [key]: {
      ...convertedValue,
      origin: `${path}.${key}`,
    },
  } as Record<K, Enriched<T>>
}

const enrichFrame = ({
  frame,
  path,
}: {
  frame: ValueFrameInOut
  path: string
}): Enriched<ValueFrameInOut> => {
  if (isEnumeratedValueFrameInOut(frame)) {
    const enrichedEntries = enrichValue({
      key: 'entries',
      value: frame.entries,
      path,
    })

    return {
      ...frame,
      origin: path,
      ...enrichedEntries,
    } as Enriched<EnumeratedValueFrameInOut>
  } else if (isRangedValueFrameInOut(frame)) {
    return {
      ...frame,
      origin: path,
      ...enrichValue({ key: 'range', value: frame.range, path }),
    } as Enriched<RangedValueFrameInOut>
  } else {
    throw new Error('Unsupported frame type')
  }
}

const enrichValidation = ({
  validation,
  path,
}: {
  validation: ValidationInOut
  path: string
}): Enriched<ValidationInOut> =>
  ({
    ...validation,
    ...enrichValue({
      key: 'validation',
      value: validation.validation,
      path,
    }),
    ...enrichValue({
      key: 'key',
      value: validation.key,
      path,
    }),
    ...enrichValue({
      key: 'defaultPattern',
      value: validation.defaultPattern,
      path,
    }),
    ...enrichValue({
      key: 'keyArguments',
      value: validation.keyArguments,
      path,
    }),
    origin: path,
  }) as Enriched<ValidationInOut>

type CommonEnrichInOutVariantArgs = {
  path: string
}

export function enrichTemplateVariant<T extends null | undefined>(
  args: CommonEnrichInOutVariantArgs & {
    value: T
  },
): T
export function enrichTemplateVariant<T extends TemplateVariantInOut>(
  args: CommonEnrichInOutVariantArgs & {
    value: T
  },
): Enriched<T>

export function enrichTemplateVariant<T extends TemplateVariantInOut>({
  value,
  path,
}: CommonEnrichInOutVariantArgs & {
  value: Optional<T>
}): Enriched<Optional<T>> {
  if (!value) {
    return value
  }

  return {
    ...value,
    ...enrichValue({
      key: 'cardinality',
      value: value.cardinality,
      path,
    }),
    ...enrichValue({
      key: 'defaultSelected',
      value: value.defaultSelected,
      path,
    }),
    ...enrichValue({
      key: 'selectable',
      value: value.selectable,
      path,
    }),
    origin: path,
  } as Enriched<T>
}

type CommonEnrichInOutFieldArgs = {
  metaModel: MetaModel
  path: string
}

export function enrichConfigurationField(
  args: CommonEnrichInOutFieldArgs & {
    value: null
  },
): null
export function enrichConfigurationField<T extends TemplateFieldValueInOut>(
  args: CommonEnrichInOutFieldArgs & {
    value: T
  },
): Enriched<T>
export function enrichConfigurationField<T extends TemplateFieldValueInOut>(
  args: CommonEnrichInOutFieldArgs & {
    value: T
  },
): Enriched<T>

export function enrichConfigurationField<T extends TemplateFieldValueInOut>({
  value,
  metaModel,
  path,
}: CommonEnrichInOutFieldArgs & {
  value: Optional<T>
}): Optional<Enriched<T>> {
  if (!value) {
    return value
  }

  if (isConfigurationFunctionValueInOut(value)) {
    const { documentation, ...other } = value

    return {
      ...other,
      origin: path,
      ...(documentation
        ? {
            documentation: {
              value: documentation,
              origin: `${path}.documentation`,
            },
          }
        : {}),
    } as Enriched<T>
  } else if (isConfigurationExpressionValueInOut(value)) {
    const { documentation, ...other } = value

    return {
      ...other,
      ...(documentation
        ? {
            documentation: {
              value: documentation,
              origin: `${path}.documentation`,
            },
          }
        : {}),
      origin: path,
    } as Enriched<T>
  } else if (isSimpleValueSetupInOut(value)) {
    const { documentation, valueConfig, ...other } = value

    return {
      ...other,
      origin: path,
      ...enrichValueSetupInCommonFields({ inOut: value, path }),
      ...enrichValue({
        key: 'defaultValue',
        value: other.defaultValue,
        path,
      }),
      ...(other.frame
        ? {
            frame: enrichFrame({
              frame: other.frame,
              path: `${path}.frame`,
            }),
          }
        : {}),
      ...(other.validations
        ? {
            validations: other.validations
              .filter(notEmpty)
              .map((validation, index) =>
                enrichValidation({
                  validation,
                  path: toIndexedPath({
                    basePath: path,
                    propName: 'validations',
                    index,
                  }),
                }),
              ),
          }
        : {}),
      ...(other.dynamicFunctions?.length
        ? {
            dynamicFunctions: other.dynamicFunctions.map((dynamicFunction) => ({
              ...dynamicFunction,
              origin: functionPath({
                basePath: path,
                source: dynamicFunction?.function ?? '',
              }),
            })),
          }
        : {}),
    } as Enriched<T>
  } else if (isTemplateValueSetupInOut(value)) {
    const { documentation, valueConfig, ...typedValue } = value
    const enrichedVariants = typedValue.variants
      ?.filter(notEmpty)
      .map((v, index) =>
        enrichTemplateVariant({
          value: v,
          path: toIndexedPath({
            basePath: path,
            propName: 'variants',
            index,
          }),
        }),
      )

    return {
      ...typedValue,
      ...enrichValueSetupInCommonFields({ inOut: value, path }),
      ...enrichValue({
        key: 'cardinality',
        value: typedValue.cardinality,
        path,
      }),
      origin: path,
      ...(typedValue.validations
        ? {
            validations: typedValue.validations
              .filter(notEmpty)
              .map((validation, index) =>
                enrichValidation({
                  validation,
                  path: toIndexedPath({
                    basePath: path,
                    propName: 'validations',
                    index,
                  }),
                }),
              ),
          }
        : {}),
      variants: enrichedVariants,
    } as Enriched<TemplateValueSetupInOut> as Enriched<T>
  } else if (isSubTemplateValueSetupInOut(value)) {
    const { documentation, valueConfig, ...typedValue } = value

    return {
      ...typedValue,
      ...enrichValueSetupInCommonFields({ inOut: value, path }),
      ...enrichValue({
        key: 'cardinality',
        value: typedValue.cardinality,
        path,
      }),
      origin: path,
      variants: typedValue.variants?.filter(notEmpty).map((v) =>
        enrichTemplate({
          metaModel,
          template: v,
          path: `${path}{${v.name}}`,
        }),
      ),
      ...(typedValue.validations
        ? {
            validations: typedValue.validations
              .filter(notEmpty)
              .map((validation, index) =>
                enrichValidation({
                  validation,
                  path: toIndexedPath({
                    basePath: path,
                    propName: 'validations',
                    index,
                  }),
                }),
              ),
          }
        : {}),
    } as Enriched<SubTemplateValueSetupInOut> as Enriched<T>
  } else {
    throw new Error(
      `Invalid enrichable field for enrichConfigurationField: ${value._type}`,
    )
  }
}

export const toIndexedPath = ({
  basePath,
  index,
  propName,
}: {
  basePath: string
  propName: string
  index: number
}) => `${basePath}.${propName}[${index}]`

const enrichValueSetupInCommonFields = ({
  inOut: { documentation, valueConfig, required, requiredInModelInstance },
  path,
}: {
  inOut: ValueSetupInOut
  path: string
}) => ({
  ...(documentation
    ? {
        documentation: {
          value: documentation,
          origin: `${path}.documentation`,
        },
      }
    : {}),
  ...(valueConfig
    ? {
        valueConfig: {
          value: valueConfig,
          origin: `${path}.valueConfig`,
        },
      }
    : {}),
  ...enrichValue({
    key: 'required',
    value: required,
    path,
  }),
  ...enrichValue({
    key: 'requiredInModelInstance',
    value: requiredInModelInstance,
    path,
  }),
})

/**
 * Creates a curried find-index function for sorted insertion of operations.
 * The returned function compares two operations first by time, then by topological
 * block depth, then by draft status.
 */
export const createOperationFindFn =
  <T extends { block: string; isDraft: boolean }>(
    getTime: (operation: T) => number,
  ) =>
  (topologicalBlockPath: string[]) =>
  (forOperation: T) =>
  (compareTo: T): boolean => {
    const compareToTime = getTime(compareTo)
    const forOperationTime = getTime(forOperation)

    if (compareToTime !== forOperationTime) {
      return compareToTime > forOperationTime
    }

    const topologicalDepth = (operation: T) =>
      topologicalBlockPath.indexOf(operation.block)

    const compareToDepth = topologicalDepth(compareTo)
    const forOperationDepth = topologicalDepth(forOperation)

    if (compareToDepth !== forOperationDepth) {
      return compareToDepth > forOperationDepth
    }

    return compareTo.isDraft && !forOperation.isDraft
  }

export const templateOperationFindFn = createOperationFindFn(
  (operation: IndexedTemplateOperationWithMetaData) => {
    const fromDate = getTemplateOperationFromDate(operation.internalOperation)
    return fromDate
      ? dateUtils.dateUtcAsLocal(fromDate).valueOf()
      : Number.NEGATIVE_INFINITY
  },
)

export const parameterDefinitionFindFn = createOperationFindFn(
  (operation: IndexedParameterDefinitionCreateOperationWithMetaData) => {
    const fromDate = operation.internalOperation.entity?.fromDate
    return fromDate
      ? dateUtils.dateUtcAsLocal(fromDate).valueOf()
      : Number.NEGATIVE_INFINITY
  },
)

/**
 * Push to an array while keeping the elements sorted.
 */
export const pushSorted = <T>({
  array,
  element,
  findIndexFn,
}: {
  array: T[]
  element: T
  findIndexFn: (element: T) => (compareTo: T) => boolean
}) => {
  const insertPosition = array.findIndex(findIndexFn(element))
  array.splice(
    insertPosition !== -1 ? insertPosition : array.length,
    0,
    element,
  )
}

export const stringToDateTimeNullSafe = (
  forDate: Optional<string>,
  fallBack: number,
) => {
  if (!forDate || forDate.trim().length === 0) {
    return fallBack
  }

  return dateUtils.dateUtcAsLocal(forDate).valueOf()
}

export const templateModelTypeFromCreateOperation = (
  operation: TemplateOperation,
): Optional<string> => {
  if (isTemplateCreateOperation(operation)) {
    const modelTypeName = operation?.entity?.modelTypeName

    assertNotEmpty(modelTypeName)

    return modelTypeName
  }

  return undefined
}

export const userActionsForOperation = (
  operation: ConfigurationOperation,
  changesetHistory: Optional<ChangesetHistory>,
): UserActionData[] => {
  const operationKey = isLargeOperation(operation)
    ? operation.innerOperationKey
    : operation.operationKey

  const approvalSet = changesetHistory?.approvalSets?.find(
    (approvalSet) =>
      operationKey && approvalSet?.operationKeys?.includes(operationKey),
  )

  const originSet = changesetHistory?.originSets?.find(
    (originSet) =>
      operationKey && originSet?.operationKeys?.includes(operationKey),
  )

  return [
    originSet
      ? ({
          action: 'create',
          userId: originSet.creatorUserId,
          actionTime: originSet.creationTime,
        } as const)
      : undefined,
    approvalSet
      ? ({
          action: 'approve',
          userId: approvalSet.approverUserId,
          actionTime: approvalSet.approvalTime,
        } as const)
      : undefined,
  ].filter(notEmpty)
}

export const convertConfigurationPackageToBranchChangesets = (
  result: Optional<ConfigurationPackage>,
  forBlock?: Optional<string>,
): Optional<BranchChangesets> =>
  result
    ? {
        _type: 'BranchChangesets',
        branchChangesets: [
          ...(result?.changesets ?? []).map((changeset) => ({
            _type: 'BranchChangeset',
            block: forBlock,
            changeset,
            changesetHistory: result?.changesetHistories?.find(
              (history) => history?.changesetKey === changeset?.changesetKey,
            ),
          })),
        ],
      }
    : undefined
